import React, { useCallback, useRef, useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Button, Card, Flex, Text } from "rebass";
import { MdSearch, MdClose, MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";
import { MarkOptions } from "mark.js";

import { getLeftSideWidth } from "../../../store/modules/display/selectors";

import { SpinnerSVG } from "../svg/SpinnerSVG";

import {
  ClassName,
  DELAY,
  HEIGHT,
  optionCollection,
  PADDING,
  SIZE
} from "./settings";

import { useDebounce } from "../../hooks/useDebounce";

interface IProps {
  currentIndex: number;
  setCurrent: React.Dispatch<React.SetStateAction<number>>;
  markLength: number;
  resetSearch(options: MarkOptions): void;
  doneSearch(): void;
  searching(value: string, options: MarkOptions): void;
}

export const FindOnPage: React.FC<IProps> = (
  {
    currentIndex,
    setCurrent,
    markLength,
    resetSearch,
    doneSearch,
    searching,
  }) => {
  const inputRef = useRef<HTMLInputElement>();
  const leftSideWidth = useSelector(getLeftSideWidth);
  const [value, setValue] = useState("");
  const [isOpen, setOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const toggleOpen = useCallback(() => {
    setOpen(prevState => !prevState);
  }, []);
  const setNext = useCallback(() => {
    setCurrent(prevState => prevState + 1);
  }, [setCurrent]);
  const setPrev = useCallback(() => {
    setCurrent(prevState => prevState - 1);
  }, [setCurrent]);
  const handleOnChange = useCallback(({ target: { value } }) => {
    setValue(value);
  }, []);
  const debounceCallback = useCallback(() => {
    const done = () => {
      setIsProcessing(false);
      doneSearch();
    };

    searching(value, { className: "find", separateWordSearch: false, done });
  }, [value, searching, doneSearch]);
  useEffect(() => {
    if (isOpen) {
      inputRef.current!.focus();
    } else {
      setValue("");
      setIsProcessing(false);
      resetSearch(optionCollection[ClassName.find]);
    }
  }, [isOpen, resetSearch]);
  useEffect(() => {
    resetSearch(optionCollection[ClassName.find]);

    if (value) {
      setIsProcessing(true);
    }
  }, [value, resetSearch]);

  useDebounce(debounceCallback, DELAY);
  const buttons = useMemo(() => [
    { id: "prev", Icon: MdKeyboardArrowUp, onClick: setPrev, disabled: currentIndex < 1 },
    { id: "next", Icon: MdKeyboardArrowDown, onClick: setNext, disabled: currentIndex > markLength - 2 }
  ], [currentIndex, markLength, setNext, setPrev]);
  const result = useMemo(() => {
    if (isProcessing) {
      return (
        <SpinnerSVG size={10} />
      );
    }

    return (
      markLength ? `${currentIndex + 1} / ${markLength}` : "- / -"
    );
  }, [isProcessing, currentIndex, markLength]);

  return (
    <Card
      mr={`-${HEIGHT + 10}px`}
      borderRadius={`${HEIGHT / 2}px`}
      border="1px solid"
      borderColor="sp-gray"
      boxShadow={isOpen ? "0 26px 24px 0 rgba(0,0,0,.21)" : "unset"}
      bg="#fff"
      css={`
        position: fixed;
        top: 30px;
        right: ${(100 - leftSideWidth) / 6}%;
        display: flex;
        overflow: hidden;
        z-index: 3;
      `}
    >
      {isOpen && (
        <Flex
          alignItems="center"
          ml={`${HEIGHT / 2}px`}
          mr={`${PADDING / 2}px`}
        >
          <Text
            ref={inputRef}
            as="input"
            height="100%"
            width={isOpen ? "150px": "0"}
            mr={`${HEIGHT / 2}px`}
            color="wild-blue"
            value={value}
            onChange={handleOnChange}
          />

          <Card
            mx={`${PADDING / 2}px`}
            px="4px"
            py="2px"
            fontSize="10px"
            color="sp-blue"
            border="1px solid"
            borderColor="sp-gray"
            borderRadius="2px"
          >
            {result}
          </Card>

          {
            buttons.map(({ id, Icon, ...props }) => (
              <Button
                key={id}
                type="button"
                px={`${PADDING / 2}px`}
                py={`${PADDING / 2}px`}
                borderRadius="50%"
                border="1px solid"
                borderColor="sp-gray"
                color="wild-blue"
                bg="transparent"
                {...props}
              >
                <Icon size={0.75 * SIZE} display="block" />
              </Button>
            ))
          }
        </Flex>
      )}

      <Button
        type="button"
        px={`${PADDING}px`}
        py={`${PADDING}px`}
        borderRadius="50%"
        color="wild-blue"
        bg="sp-gray"
        onClick={toggleOpen}
      >
        {isOpen
          ? <MdClose size={SIZE} display="block" />
          : <MdSearch size={SIZE} display="block" />
        }
      </Button>
    </Card>
  );
};
